import { inject } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, EMPTY, filter, map, of, switchMap, tap } from 'rxjs';

import { ADB2CErrors } from '@evc/platform';

import { passwordResetStatus } from '@app/types/password-redirect.type';

/**
 * Special guard to handle pasword reset redirect
 *
 * MSAL handle resetPassword like a login with specific authority (user journey)
 * Because we do not treat this as a login, we need to handle it differently :
 * - special guard here to handle redirect response
 * - then simply redirect back to original page with state success/error to display toast
 *
 * for more info : [Status / Password reset flow](https://evident-products.atlassian.net/wiki/spaces/WSCLOUD/pages/111772838/ADR+-+Authentification+--+Frontend+implementation)
 */
export const ResetPasswordRedirectGuard: CanActivateFn = () => {
  const msal = inject(MsalService);

  return msal.handleRedirectObservable().pipe(
    tap((result) => {
      if (!result) {
        window.location.href = '/';
      }
    }),
    filter((result) => !!result),
    map(() => true),
    catchError((_error) => {
      // MSAL keep redirectResponse in cache - if not removed any next call will fail

      const redirectResponse = (
        msal.instance as unknown as {
          controller: { redirectResponse: Map<string, unknown> };
        }
      ).controller.redirectResponse;

      for (const [key] of redirectResponse.entries()) {
        if (key.includes(ADB2CErrors.CANCELED_OPERATION)) {
          redirectResponse.delete(key);

          return of(passwordResetStatus.CANCELED);
        }
      }

      return of(false);
    }),
    tap((result) => {
      const status = (() => {
        switch (result) {
          case true:
            return passwordResetStatus.SUCCESS;
          case false:
            return passwordResetStatus.ERROR;
          default:
            return result;
        }
      })();
      window.location.href = `/security-privacy?status=${status}`;
    }),
    // never resolve since we use redirect
    switchMap(() => EMPTY),
  );
};
